import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IconButton } from '@cian/frontend-reviews-components';
import { IconSocialDislikeOn16, IconSocialLikeOn16 } from '@cian/ui-kit-design-tokens/icons';

import { Footer } from '../../../components/Footer';
import { getAnswerById } from '../../../selectors/answers';
import { EStatus } from '../../../types/likes';
import { setAnswerReaction } from '../../../actions/answers';
import { getIsAuthenticated } from '../../../selectors/user';
import { requestAuthentication } from '../../../actions/authentication';
import { NewAnswerButtonContainer } from '../../NewAnswerButton';
import { getIsReviewTab } from '../../../selectors/tabs';
import { setQuestionAnswerReaction } from '../../../actions/questionAnswers';

interface IFooterContainerProps {
  answerId: string;
}

export function FooterContainer({ answerId }: IFooterContainerProps): React.JSX.Element {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(getIsAuthenticated);
  const isReview = useSelector(getIsReviewTab);
  const answer = useSelector(getAnswerById(answerId));

  const setReactionFn = isReview ? setAnswerReaction : setQuestionAnswerReaction;

  const setAnswerLike = React.useCallback(
    () => setReactionFn({ answerId, type: EStatus.Liked }),
    [answerId, setReactionFn],
  );
  const setAnswerDisliked = React.useCallback(
    () => setReactionFn({ answerId, type: EStatus.Disliked }),
    [answerId, setReactionFn],
  );

  const onLikeClick = React.useCallback(() => {
    if (isAuthenticated) {
      dispatch(setAnswerLike());
    } else {
      dispatch(requestAuthentication({ onSuccess: setAnswerLike }));
    }
  }, [dispatch, isAuthenticated, setAnswerLike]);

  const onDislikedClick = React.useCallback(() => {
    if (isAuthenticated) {
      dispatch(setAnswerDisliked());
    } else {
      dispatch(requestAuthentication({ onSuccess: setAnswerDisliked }));
    }
  }, [dispatch, isAuthenticated, setAnswerDisliked]);

  return (
    <Footer
      like={
        <IconButton
          Icon={IconSocialLikeOn16}
          active={answer.like.status === EStatus.Liked}
          onClick={onLikeClick}
          label={answer.like.likesCount}
        />
      }
      dislike={
        <IconButton
          Icon={IconSocialDislikeOn16}
          active={answer.like.status === EStatus.Disliked}
          onClick={onDislikedClick}
          label={answer.like.dislikesCount}
        />
      }
      answer={<NewAnswerButtonContainer type="answer" targetId={answerId} />}
    />
  );
}
